import { reactular } from '@vendhq/reactular'
import React, { useEffect, useMemo, useState } from 'react'
import IMAGES_CDN_PATH from '@vend/images-ui'
import { useT } from '@transifex/react'
import { Link, Text } from '@lightspeed/design-system-react'
import { WEB_REGISTER } from '../web-register.constant'
import {
  usePosStateManager,
  useWebRegisterStatusManager,
} from '../../ng-compat/hooks'
import { EVENTS } from '../../constants'
type LineMessageProps = { isChecked: boolean; message: string }

const LineItem = ({ isChecked, message }: LineMessageProps) => {
  const lineCss = isChecked
    ? 'wr-offline-requirement-check'
    : 'wr-offline-requirement-cross'
  const iconCss = isChecked ? 'fa fa-check' : 'fa fa-times'
  return (
    <li className={lineCss}>
      <i className={iconCss}></i>
      {message}
    </li>
  )
}

export const Offline = () => {
  const offlineImgSrc = useMemo(() => `${IMAGES_CDN_PATH}sell/error-v1.svg`, [])
  const webRegisterStatusManager = useWebRegisterStatusManager()
  const posStateManager = usePosStateManager()
  const [hasCompletedSync, setHasCompletedSync] = useState(false)
  const [hasLoggedInUser, setHasLoggedInUser] = useState(false)
  const t = useT()

  useEffect(() => {
    const checkPosStateManager = async () => {
      await posStateManager.init()
      const syncCompleted = posStateManager.getInitialSyncComplete()
      const loggedInUser = posStateManager.getUser()

      setHasCompletedSync(!!syncCompleted)
      setHasLoggedInUser(!!loggedInUser)
    }
    checkPosStateManager()
  }, [posStateManager])

  useEffect(() => {
    // When web register back online go back to the main application.
    const onOnline = () => window.location.replace(WEB_REGISTER.URL)
    const onlineEvent = EVENTS.transitionOnline

    const checkWebRegisterStatusManager = async () => {
      await webRegisterStatusManager.init()
      // In case user refreshed the page and weggie was online
      if (webRegisterStatusManager.isOnline()) {
        onOnline()
      }

      webRegisterStatusManager.on(onlineEvent, onOnline)
    }

    checkWebRegisterStatusManager()

    return () => {
      webRegisterStatusManager.off(onlineEvent, onOnline)
    }
  }, [webRegisterStatusManager])

  return (
    <div className="wr-error-page">
      <img
        className="wr-offline-notice-icon"
        alt="offline notice"
        src={offlineImgSrc}
      ></img>
      <h1 className="wr-offline-notice-heading vd-text-heading">
        {t('Unable to load Register', { _context: 'Header' })}
      </h1>
      <div className="wr-offline-notice-body">
        {t('Not sure why? Check your Internet connection and', {
          _context: 'Body',
        })}{' '}
        <Link
          data-track="retail-system-status"
          href={WEB_REGISTER.VEND_STATUS_URL}
          isExternal={true}
        >
          {t(`Lightspeed Retail's system status`, { _context: 'Link' })}
        </Link>
        .
      </div>
      <div className="wr-offline-requirements">
        <Text>
          {t(
            'Trying to run Lightspeed Retail Register offline? Make sure you have:',
            { _context: 'Paragraph' }
          )}
        </Text>
        <div>
          <ul className="wr-offline-requirement-list">
            <LineItem
              isChecked={hasCompletedSync}
              message={
                ' ' +
                t('Loaded full store in this browser before', {
                  _context: 'Info',
                })
              }
            />
            <LineItem
              isChecked={hasLoggedInUser}
              message={
                ' ' +
                t('At least one user logged in', {
                  _context: 'Info',
                })
              }
            />
          </ul>
          <Text>
            {t(
              `Right now, you can not run Lightspeed Retail Register offline`,
              {
                _context: 'Info',
              }
            )}
          </Text>
        </div>
      </div>
    </div>
  )
}

export const OfflinePage = reactular(Offline, [], 'reactWrapper')
